import { DateTime } from 'luxon';
import { customerApi, QueryTag } from '../api';
import {
  AssetGIFilters,
  AssetVIFilters,
  PerformanceGIAssetsFilters,
  PerformanceVIAssetsFilters,
} from './assetsApi';
import { CustomFilterParams, getUrlParams, QueryParams } from './helpers';

export interface HeatmapItem {
  lat: number;
  lon: number;
  int: number;
}

export type MapLocationParams = Partial<CustomFilterParams>;

interface HeatmapQueryParams extends MapLocationParams {
  timeRange?: QueryParams['timeRange'];
  assetIds?: QueryParams['assetIds'];
}

interface HeatmapAssetVIQueryParams extends AssetVIFilters, HeatmapQueryParams {
  assetIds: string;
}

interface HeatmapAssetGIQueryParams extends AssetGIFilters, HeatmapQueryParams {
  assetIds: string;
}

interface HeatmapPerformanceVIQueryParams extends PerformanceVIAssetsFilters, HeatmapQueryParams {
  assetIds: undefined;
}

interface HeatmapPerformanceGIQueryParams extends PerformanceGIAssetsFilters, HeatmapQueryParams {
  assetIds: undefined;
}

type HeatmapQueryType =
  | HeatmapAssetVIQueryParams
  | HeatmapAssetGIQueryParams
  | HeatmapPerformanceVIQueryParams
  | HeatmapPerformanceGIQueryParams;

interface HeatmapQueryModel {
  query: HeatmapQueryType;
}

interface HeatmapResponse {
  sLat: number | null;
  sLon: number | null;
  eLat: number | null;
  eLon: number | null;
  cellWidth: number | null;
  items: HeatmapItem[];
}

export interface TransformedHeatmapResponse extends HeatmapResponse {
  args: MapLocationParams & { queryKey: string };
}

const transformHeatmapResponse = (
  response: HeatmapResponse,
  meta: object | undefined,
  arg: HeatmapQueryModel,
): TransformedHeatmapResponse => ({
  ...response,
  args: {
    startLat: arg.query.startLat,
    startLon: arg.query.startLon,
    endLat: arg.query.endLat,
    endLon: arg.query.endLon,
    zoom: arg.query.zoom,
    // Add a unique key to the response to inform the app that the user has made a fresh request.
    queryKey: DateTime.now().toMillis().toString(),
  },
});

export const getHeatmapTag = (interactionsGroup: 'VI' | 'GI', assetId?: string) => ({
  type: QueryTag.Heatmap,
  id: assetId ? `${assetId}--${interactionsGroup}` : interactionsGroup,
});

export const heatmapsApi = customerApi.injectEndpoints({
  endpoints: build => {
    return {
      getVIHeatmap: build.query<TransformedHeatmapResponse, HeatmapQueryModel>({
        query: ({ query }) => ({
          url: 'api/heatmaps/vehicleinteractions',
          params: getUrlParams(query),
        }),
        transformResponse: transformHeatmapResponse,
        providesTags: (result, error, args) =>
          result ? [QueryTag.SiteCache, getHeatmapTag('VI', args.query.assetIds)] : [],
      }),
      getGIHeatmap: build.query<TransformedHeatmapResponse, HeatmapQueryModel>({
        query: ({ query }) => ({
          url: 'api/heatmaps/geofenceinteractions',
          params: getUrlParams(query),
        }),
        transformResponse: transformHeatmapResponse,
        providesTags: (result, error, args) =>
          result ? [QueryTag.SiteCache, getHeatmapTag('GI', args.query.assetIds)] : [],
      }),
    };
  },
  overrideExisting: false,
});

export const { useGetVIHeatmapQuery, useGetGIHeatmapQuery } = heatmapsApi;
